// 9fbef606107a605d69c0edbcd8029e5d
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { StyledCarousel, StyledScroll, StyledGrid } from '../styles/Carousel.style';

const useIntersectionObserver = (options = {}, defaultVisibility = false) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(defaultVisibility);

  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [containerRef?.current?.getBoundingClientRect(), options]);

  return [containerRef, isVisible];
};

const renderArrowButton = (showArrow, isVisible, onScrollClick, classNameVal) => {
  return showArrow && isVisible ? (
    <button onClick={onScrollClick} className={`arrow ${classNameVal}`} />
  ) : null;
};

const Carousel = (props) => {
  const {
    children,
    isArrowVisible,
    tileRef,
    defaultTileWidth,
    slidesToScroll,
    isCategoryTab,
    customCls,
    customClassNameOnChildBlock,
    customLeftArrowClassName,
    customRightArrowClassName,
    ref,
    containerRef,
    moduleGBundleVariation,
  } = props;
  const fallbackRef = useRef(null);
  const scrollContainerRef = moduleGBundleVariation ? containerRef : fallbackRef;
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [scrollEndRef, isEndTileInView] = useIntersectionObserver({
    root: scrollContainerRef.current,
    rootMargin: '0px 40px 0px 0px',
  });
  const [scrollStartRef, isStartTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });

  useEffect(() => {
    if (!tileRef) return; // Early return if tileRef is not available

    const isStartVisible = isStartTileInView;
    const isEndVisible = isEndTileInView;
    // If both start and end tiles are visible, hide both arrows
    if (isStartVisible && isEndVisible) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
    } else {
      // Show left arrow if the start tile is not visible
      setShowLeftArrow(!isStartVisible);
      // Show right arrow if the end tile is not visible
      setShowRightArrow(!isEndVisible);
    }
  }, [isStartTileInView, isEndTileInView]);

  const onScrollNext = useCallback(() => {
    const oneTileWidth = tileRef?.current?.offsetWidth || defaultTileWidth;
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft += slidesToScroll * oneTileWidth;
    }
  });

  const onScrollPrev = useCallback(() => {
    const oneTileWidth = tileRef?.current?.offsetWidth || defaultTileWidth;
    if (scrollContainerRef) {
      scrollContainerRef.current.scrollLeft -= oneTileWidth * slidesToScroll;
    }
  });

  return (
    <StyledCarousel isCategoryTab={isCategoryTab} className={customCls} ref={ref}>
      <div className={`carousel-arrows ${customLeftArrowClassName}`}>
        {renderArrowButton(showLeftArrow, isArrowVisible, onScrollPrev, 'left-arrow')}
      </div>
      <StyledScroll ref={scrollContainerRef}>
        <StyledGrid
          className={`carousel-content ${customClassNameOnChildBlock}`}
          isCategoryTab={isCategoryTab}
        >
          <div ref={scrollStartRef} />
          {children}
          <div ref={scrollEndRef} />
        </StyledGrid>
      </StyledScroll>
      <div className={`carousel-arrows ${customRightArrowClassName}`}>
        {renderArrowButton(showRightArrow, isArrowVisible, onScrollNext, 'right-arrow')}
      </div>
    </StyledCarousel>
  );
};
Carousel.propTypes = {
  children: PropTypes.node,
  isArrowVisible: PropTypes.bool,
  tileRef: PropTypes.shape({}),
  slidesToScroll: PropTypes.number,
  defaultTileWidth: PropTypes.number,
  isCategoryTab: PropTypes.bool,
  customClassNameOnChildBlock: PropTypes.string,
  customLeftArrowClassName: PropTypes.string,
  customRightArrowClassName: PropTypes.string,
  moduleGBundleVariation: PropTypes.bool,
};
Carousel.defaultProps = {
  children: null,
  isArrowVisible: false,
  tileRef: {},
  slidesToScroll: 3,
  defaultTileWidth: 283,
  isCategoryTab: false,
  customClassNameOnChildBlock: '',
  customLeftArrowClassName: '',
  customRightArrowClassName: '',
  moduleGBundleVariation: false,
};
export default Carousel;
